/**
 * Shopify Settings — /settings/shopify
 *
 * Admin-only page to connect Shopify stores (OAuth or Direct-Token), view
 * connection status, run product syncs, and toggle read-only vs two-way mode.
 */
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2, ExternalLink, Loader2, Plug, RefreshCw, ShieldCheck, Store,
  Trash2, X, Info,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
  DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";

interface ShopifyConfig {
  oauth_configured: boolean;
  api_version: string;
  scopes: string;
}
interface ShopifyConnection {
  shop: string;
  mode: "oauth" | "direct";
  scopes: string;
  connected_at: string;
  updated_at: string;
  last_synced_at?: string;
  sync_mode: "readonly" | "two_way";
  shop_info?: Record<string, string>;
  product_count?: number;
}

export const ShopifySettings: React.FC = () => {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);

  const config = useQuery<ShopifyConfig>({
    queryKey: ["shopify-config"],
    queryFn: async () => (await api.get("/shopify/config")).data,
  });

  const connections = useQuery<ShopifyConnection[]>({
    queryKey: ["shopify-connections"],
    queryFn: async () => (await api.get("/shopify/connections")).data,
  });

  return (
    <DashboardLayout
      title="Shopify"
      subtitle="One-click product sync from your Shopify store into SociBaba."
      seoPath="/settings/shopify"
      seoDescription="Connect your Shopify store and pull products, variants, and inventory into SociBaba."
      actions={
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron-hover)] text-white"
              data-testid="shopify-connect-btn"
            >
              <Plug className="h-4 w-4 mr-1.5" /> Connect Shopify
            </Button>
          </DialogTrigger>
          <ConnectDialog
            config={config.data}
            onDone={() => {
              setDialogOpen(false);
              qc.invalidateQueries({ queryKey: ["shopify-connections"] });
            }}
          />
        </Dialog>
      }
    >
      {/* Config banner */}
      {config.data && !config.data.oauth_configured && (
        <div className="mb-6 p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm flex items-start gap-3" data-testid="shopify-oauth-banner">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">OAuth not configured on the server yet.</div>
            <div className="mt-1">
              For self-serve OAuth ("Connect Shopify" flow used by merchants), set <code className="text-xs bg-white/60 px-1 rounded">SHOPIFY_CLIENT_ID</code>, <code className="text-xs bg-white/60 px-1 rounded">SHOPIFY_CLIENT_SECRET</code>, and <code className="text-xs bg-white/60 px-1 rounded">APP_BASE_URL</code> env vars.
              For now, use the <strong>Direct Token</strong> mode which works with any private/custom Shopify app.
            </div>
          </div>
        </div>
      )}

      {/* Connections */}
      {connections.isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="sg-skeleton h-40" /><div className="sg-skeleton h-40" />
        </div>
      ) : connections.data && connections.data.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {connections.data.map((c) => <ConnectionCard key={c.shop} c={c} />)}
        </div>
      ) : (
        <div className="sg-card p-10 text-center" data-testid="shopify-empty">
          <Store className="h-8 w-8 mx-auto text-[color:var(--sg-muted)] mb-3" />
          <div className="font-display text-xl font-bold tracking-tight">No stores connected yet</div>
          <div className="text-sm text-[color:var(--sg-muted)] mt-2 max-w-md mx-auto">
            Connect your <strong>myshopify.com</strong> store and we'll pull every product, variant, and inventory count into SociBaba — usually in under a minute.
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

// ------------------------------------------------------------------
// Connection card
// ------------------------------------------------------------------
const ConnectionCard: React.FC<{ c: ShopifyConnection }> = ({ c }) => {
  const qc = useQueryClient();

  const sync = useMutation({
    mutationFn: async () => (await api.post(`/shopify/connections/${c.shop}/sync`)).data,
    onSuccess: (d: { imported: number; updated: number }) => {
      toast.success(`Sync complete — ${d.imported} added, ${d.updated} updated`);
      qc.invalidateQueries({ queryKey: ["shopify-connections"] });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Sync failed"),
  });

  const disconnect = useMutation({
    mutationFn: async () => (await api.delete(`/shopify/connections/${c.shop}`)).data,
    onSuccess: () => {
      toast.success(`Disconnected ${c.shop}`);
      qc.invalidateQueries({ queryKey: ["shopify-connections"] });
    },
  });

  const toggleTwoWay = useMutation({
    mutationFn: async (mode: "readonly" | "two_way") =>
      (await api.patch(`/shopify/connections/${c.shop}/sync-mode`, { sync_mode: mode })).data,
    onSuccess: (_, mode) => {
      toast.success(`Sync mode set to ${mode === "two_way" ? "two-way" : "read-only"}`);
      qc.invalidateQueries({ queryKey: ["shopify-connections"] });
    },
  });

  const lastSynced = c.last_synced_at
    ? new Date(c.last_synced_at).toLocaleString()
    : "Never";
  const info = c.shop_info || {};

  return (
    <div className="sg-card p-5 flex flex-col" data-testid={`shopify-card-${c.shop}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-[color:var(--sb-saffron)]/10 flex items-center justify-center">
              <Store className="h-4 w-4 text-[color:var(--sb-saffron)]" />
            </div>
            <div>
              <div className="font-semibold text-sm">{info.name || c.shop}</div>
              <a
                href={`https://${c.shop}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-[color:var(--sg-muted)] hover:underline inline-flex items-center gap-1"
              >
                {c.shop} <ExternalLink className="h-2.5 w-2.5" />
              </a>
            </div>
          </div>
        </div>
        <span className="sg-chip bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
          <CheckCircle2 className="h-3 w-3" /> Connected · {c.mode === "direct" ? "Token" : "OAuth"}
        </span>
      </div>

      <dl className="grid grid-cols-3 gap-3 mt-5 text-xs">
        <div>
          <dt className="text-[color:var(--sg-muted)] uppercase tracking-wider mb-1">Products</dt>
          <dd className="font-semibold text-base">{c.product_count ?? 0}</dd>
        </div>
        <div>
          <dt className="text-[color:var(--sg-muted)] uppercase tracking-wider mb-1">Plan</dt>
          <dd className="font-semibold">{info.plan_name || "—"}</dd>
        </div>
        <div>
          <dt className="text-[color:var(--sg-muted)] uppercase tracking-wider mb-1">Currency</dt>
          <dd className="font-semibold">{info.currency || "—"}</dd>
        </div>
      </dl>

      <div className="mt-4 text-xs text-[color:var(--sg-muted)]">
        Last sync: <span className="text-[color:var(--sg-fg)] font-medium">{lastSynced}</span>
      </div>

      <div className="mt-4 flex items-center justify-between p-3 rounded-lg bg-[color:var(--sg-muted-bg)]/50 border border-[color:var(--sg-border)]">
        <div>
          <div className="font-semibold text-xs">Two-way sync</div>
          <div className="text-[10px] text-[color:var(--sg-muted)]">Push edits from SociBaba back to Shopify</div>
        </div>
        <Switch
          checked={c.sync_mode === "two_way"}
          onCheckedChange={(v) => toggleTwoWay.mutate(v ? "two_way" : "readonly")}
          data-testid={`shopify-two-way-${c.shop}`}
        />
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          className="flex-1 bg-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron-hover)] text-white"
          data-testid={`shopify-sync-${c.shop}`}
        >
          {sync.isPending ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Syncing…</> : <><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Sync now</>}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            if (window.confirm(`Disconnect ${c.shop}? Products will remain but stop syncing.`)) {
              disconnect.mutate();
            }
          }}
          disabled={disconnect.isPending}
          className="text-red-600 hover:bg-red-50"
          data-testid={`shopify-disconnect-${c.shop}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// Connect dialog — OAuth OR Direct Token
// ------------------------------------------------------------------
const ConnectDialog: React.FC<{ config?: ShopifyConfig; onDone: () => void }> = ({
  config, onDone,
}) => {
  const [tab, setTab] = useState<"oauth" | "direct">(config?.oauth_configured ? "oauth" : "direct");
  const [shop, setShop] = useState("");
  const [token, setToken] = useState("");

  const startOAuth = useMutation({
    mutationFn: async () => (await api.get(`/shopify/oauth/start?shop=${encodeURIComponent(shop)}`)).data,
    onSuccess: (d: { authorize_url: string }) => {
      window.location.href = d.authorize_url;
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Could not start OAuth"),
  });

  const directConnect = useMutation({
    mutationFn: async () =>
      (await api.post("/shopify/connect/direct", { shop, access_token: token })).data,
    onSuccess: () => {
      toast.success(`Connected ${shop}`);
      onDone();
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Connection failed"),
  });

  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="font-display tracking-tight">Connect Shopify</DialogTitle>
        <DialogDescription>Pull your entire product catalog into SociBaba — variants, inventory, and images.</DialogDescription>
      </DialogHeader>
      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList className="grid grid-cols-2">
          <TabsTrigger value="oauth" disabled={!config?.oauth_configured} data-testid="shopify-tab-oauth">
            OAuth {!config?.oauth_configured && <span className="text-[10px] ml-1 opacity-60">(setup req)</span>}
          </TabsTrigger>
          <TabsTrigger value="direct" data-testid="shopify-tab-direct">Direct token</TabsTrigger>
        </TabsList>

        <TabsContent value="oauth" className="space-y-4 pt-4">
          <div>
            <Label>Store domain</Label>
            <div className="flex items-center gap-1 mt-1">
              <Input
                value={shop}
                onChange={(e) => setShop(e.target.value)}
                placeholder="your-store"
                data-testid="shopify-oauth-shop"
              />
              <span className="text-sm text-[color:var(--sg-muted)]">.myshopify.com</span>
            </div>
          </div>
          <Button
            onClick={() => startOAuth.mutate()}
            disabled={!shop.trim() || startOAuth.isPending}
            className="w-full bg-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron-hover)] text-white"
            data-testid="shopify-oauth-start"
          >
            {startOAuth.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</> : "Continue on Shopify"}
          </Button>
        </TabsContent>

        <TabsContent value="direct" className="space-y-4 pt-4">
          <div className="text-xs text-[color:var(--sg-muted)] p-3 rounded-lg bg-[color:var(--sg-muted-bg)]/50 flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              Create a <strong>Custom App</strong> in Shopify Admin → Settings → Apps → Develop apps → grant the Admin API scopes → install → copy the <code className="text-[10px]">shpat_…</code> Admin API access token.
            </div>
          </div>
          <div>
            <Label>Store domain</Label>
            <div className="flex items-center gap-1 mt-1">
              <Input
                value={shop}
                onChange={(e) => setShop(e.target.value)}
                placeholder="your-store"
                data-testid="shopify-direct-shop"
              />
              <span className="text-sm text-[color:var(--sg-muted)]">.myshopify.com</span>
            </div>
          </div>
          <div>
            <Label>Admin API access token</Label>
            <Input
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="shpat_…"
              type="password"
              className="mt-1 font-mono text-sm"
              data-testid="shopify-direct-token"
            />
          </div>
          <Button
            onClick={() => directConnect.mutate()}
            disabled={!shop.trim() || !token.trim() || directConnect.isPending}
            className="w-full bg-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron-hover)] text-white"
            data-testid="shopify-direct-connect"
          >
            {directConnect.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying…</> : "Connect store"}
          </Button>
        </TabsContent>
      </Tabs>
    </DialogContent>
  );
};

export default ShopifySettings;
